// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Rectangle } from '@shared/commons/Geometry';
import { TerceraChartUtils } from '../TerceraChartUtils';
import { TerceraChartRendererDrawingAdvancedParamsEnum, TerceraChartToolRendererDrawingType } from '../Utils/ChartConstants';
import { Pen, SolidBrush } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { LayersEnum } from '../Utils/LayersEnum';
export class TerceraChartWindowBase {
    constructor(rightYScaleRenderer, leftYScaleRenderer) {
        this.PaddingRenderers = [];
        this.RendPaddingTop = 0;
        this.RendPaddingBottom = 0;
        this.RendPaddingLeft = 0;
        this.RendPaddingRight = 0;
        this.AutoScalePaddPixel = 20;
        this.HeaderVisible = false;
        this.IsMainWindow = false;
        this.HeaderHeight = 34;
        this.IndicatorHeaderHeight = 20;
        this.subHeaderBorderWidth = 1;
        this.PaddingLeft = 0;
        this.PaddingTop = 0;
        this.PaddingRight = 40;
        this.PaddingBottom = 0;
        /// <summary>
        /// Индекс крайнего правого отображаемого бара
        /// </summary>
        this.i1 = 0;
        /// <summary>
        /// Масштаб по оси X (px на 1 бар)
        /// </summary>
        this.XScale = 6;
        /// <summary>
        /// Мин/Макс по шкале Y
        /// </summary>
        this.FminFloatX = 0;
        this.FmaxFloatX = 0;
        this.FminFloatY = 0;
        this.FmaxFloatY = 0;
        this.autoscaleMinDelta = 1;
        this.autoscaleMaxDelta = 1;
        this.Rectangle = new Rectangle();
        this.MainThreadRenderers = [];
        this.Renderers = [];
        this.HeightKoef = 100;
        this.AutoScale = true;
        this.needRecalcAutoscale = false;
        this.SCALE_LIMIT = 100000000;
        this.ClientRectangle = new Rectangle();
        this.Collapsed = false;
        this.prevScaleW = 0;
        this.collapserRectangle = Rectangle.Empty();
        this.windowHeaderRectangle = Rectangle.Empty();
        this.isCollapsedHover = false;
        this.headerBorderRadius = 4;
        this.pointerHeight = 22;
        this.IndicatorBackBrush = null;
        this.collapserWindowDefaultImage = null;
        this.collapserWindowHoveredImage = null;
        this.expanderWindowDefaultImage = null;
        this.expanderWindowHoveredImage = null;
        this.MIN_X_SCALE = 1.0;
        this.MAX_X_SCALE = 200.0;
        this.MIN_X_SCALE_LEVEL = 0.01;
        this.NUM_ZOOM_LEVELS = 15;
        this.ZOOM_LEVELS = this.ComputeZoomLevels();
        this.rightYScaleRenderer = rightYScaleRenderer;
        if (rightYScaleRenderer !== null && rightYScaleRenderer !== undefined) {
            this.Renderers.push(rightYScaleRenderer);
            rightYScaleRenderer.window = this;
        }
        this.leftYScaleRenderer = leftYScaleRenderer;
        if (leftYScaleRenderer !== null && leftYScaleRenderer !== undefined) {
            this.Renderers.push(this.leftYScaleRenderer);
            leftYScaleRenderer.window = this;
        }
    }
    ThemeChanged() {
        this.IndicatorBackBrush = new SolidBrush(ThemeManager.colors.chartIndicatorBack);
        this.collapserWindowDefaultImage = ThemeManager.NewImages.chartPlateCollapsedArrowUpDefault;
        this.collapserWindowHoveredImage = ThemeManager.NewImages.chartPlateCollapsedArrowUpHovered;
        this.expanderWindowDefaultImage = ThemeManager.NewImages.chartPlateCollapsedArrowDownDefault;
        this.expanderWindowHoveredImage = ThemeManager.NewImages.chartPlateCollapsedArrowDownHovered;
        const renderers = this.GetAllRenderers();
        const len = renderers.length;
        for (let i = 0; i < len; i++) {
            renderers[i].ThemeChanged();
        }
    }
    Localize() {
        const renderers = this.Renderers;
        const len = renderers.length;
        for (let i = 0; i < len; i++) {
            renderers[i].Localize();
        }
    }
    GetRenderer(type) {
        const res = null;
        const arr = this.Renderers;
        let i;
        const len = arr.length;
        for (i = 0; i < len; i++) {
            if (arr[i] instanceof type) {
                return arr[i];
            }
        }
        return res;
    }
    GetRenderers(rendererType) {
        const renderers = [];
        const renderersCount = this.Renderers.length;
        for (let i = 0; i < renderersCount; i++) {
            if (this.Renderers[i] instanceof rendererType) {
                renderers.push(this.Renderers[i]);
            }
        }
        return renderers;
    }
    GetAllRenderers() {
        return this.MainThreadRenderers.concat(this.Renderers);
    }
    im() {
        return this.ClientRectangle.Width / this.XScale;
    }
    UpdateClientRectangle() {
        this.ClientRectangle.X = this.Rectangle.X + this.PaddingLeft + this.RendPaddingLeft;
        this.ClientRectangle.Y = this.Rectangle.Y + this.PaddingTop + this.RendPaddingTop;
        this.ClientRectangle.Width =
            this.Rectangle.Width - this.PaddingLeft - this.PaddingRight - this.RendPaddingLeft - this.RendPaddingRight;
        this.ClientRectangle.Height =
            this.Rectangle.Height - this.PaddingTop - this.PaddingBottom - this.RendPaddingTop - this.RendPaddingBottom;
    }
    /// <summary>
    /// Расчёты выполняемы перед прорисовкой окнв
    /// </summary>
    BeforeDrawing(advParams = null) {
        this.CalcScales();
        // Сбрасываем параметр на дефолт
        const par = advParams;
        if (par != null) {
            // to think
            this.OnLayout(par.LayoutInfo);
            par.Tag[TerceraChartRendererDrawingAdvancedParamsEnum.CurrentDrawingType] =
                TerceraChartToolRendererDrawingType.Background;
            // каждое окно рисует само
            // par.DrawPointers.Clear();
        }
    }
    OnLayout(info = null) {
        this.PaddingTop = 0;
        this.HeaderVisible = false;
        if (!isNullOrUndefined(info)) {
            this.PaddingRight = info.PreferredWidthScales;
            this.PaddingLeft = info.PreferredWidthScalesLeft;
        }
        this.UpdateClientRectangle();
        const clientR = this.ClientRectangle;
        if (!isNullOrUndefined(this.rightYScaleRenderer)) {
            this.rightYScaleRenderer.Rectangle = new Rectangle(clientR.X + clientR.Width, clientR.Y - this.PaddingTop, this.PaddingRight, clientR.Height + this.PaddingTop);
        }
        if (!isNullOrUndefined(this.leftYScaleRenderer)) {
            this.leftYScaleRenderer.Rectangle = new Rectangle(clientR.X - this.PaddingLeft, clientR.Y - this.PaddingTop, this.PaddingLeft, clientR.Height + this.PaddingTop);
        }
        this.LayoutPaddingRenderers();
    }
    CalcScales() {
        if (this.FmaxFloatY === this.FminFloatY) {
            this.YScale = 0;
        }
        else {
            this.YScale = this.ClientRectangle.Height / (this.FmaxFloatY - this.FminFloatY);
            if (this.previousYScale !== this.YScale) {
                this.previousYScale = this.YScale;
                return true;
            }
        }
    }
    CalculateMinMax() {
        try {
            if (!this.AutoScale && !this.needRecalcAutoscale) {
                return;
            }
            if (this.needRecalcAutoscale) {
                this.needRecalcAutoscale = false;
            }
            let newFminFloatY = Number.MAX_VALUE;
            let newFmaxFloatY = -Number.MAX_VALUE;
            const tMin = Number.MAX_VALUE;
            const tMax = -Number.MAX_VALUE;
            const rendLen = this.Renderers.length;
            for (let i = 0; i < rendLen; i++) {
                const refObj = {};
                refObj.tMin = tMin;
                refObj.tMax = tMax;
                if (this.Renderers[i].UseInAutoscale && this.Renderers[i].FindMinMax(refObj, this)) {
                    if (refObj.tMin < newFminFloatY) {
                        newFminFloatY = refObj.tMin;
                    }
                    if (refObj.tMax > newFmaxFloatY) {
                        newFmaxFloatY = refObj.tMax;
                    }
                }
            }
            if (newFminFloatY === Number.MAX_VALUE && newFmaxFloatY === -Number.MAX_VALUE) {
                // Никто не дал мин/макс - оставляем предыдущее значения
                // (пример: скрытие всех линий индикатора на дополнительно окне)
            }
            else {
                // Женя: если так совпало что мин и макс одинаковы... чтоб не было на шкале вечного нуля
                if (newFminFloatY === newFmaxFloatY) {
                    newFminFloatY -= this.autoscaleMinDelta;
                    newFmaxFloatY += this.autoscaleMaxDelta;
                }
                //
                // Add small padding for autoscale
                // pixels convert to price and change Min/Max
                if (newFminFloatY !== newFmaxFloatY) {
                    const minMaxDelta = Math.abs(newFmaxFloatY - newFminFloatY);
                    const clientH = this.ClientRectangle.Height;
                    const scalePadding = clientH && minMaxDelta ? this.AutoScalePaddPixel / (clientH / minMaxDelta) : 1;
                    newFminFloatY -= scalePadding;
                    newFmaxFloatY += scalePadding;
                }
                // replace values
                this.FminFloatY = newFminFloatY;
                this.FmaxFloatY = newFmaxFloatY;
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            // Utils.log(ex);
        }
    }
    AutoFit() {
        this.needRecalcAutoscale = true;
    }
    /// <summary>
    ///
    /// </summary>
    Draw(gr, windowsContainer, advParams) {
        this.BeforeDrawing(advParams);
        const curLayer = advParams.layerId;
        this.drawWindowHeader(gr, curLayer);
        // First draw container renderes
        const len = this.Renderers.length;
        for (let i = 0; i < len; i++) {
            const curRend = this.Renderers[i];
            // TODO. Move "!this.Collapsed" out from the loop.
            if (!this.Collapsed || curRend.plateRenderer || curRend.priceScaleRenderer) {
                if (curRend.IsNeedDraw(curLayer)) {
                    curRend.Draw(gr, this, windowsContainer, advParams);
                }
            }
        }
        const par = advParams;
        if (par) {
            const drawPointers = par.DrawPointers;
            // drawPointers.Sort(new DrawPointerComparer());
            const len = drawPointers.length;
            for (let i = 0; i < len; i++) {
                const pointer = drawPointers[i];
                const priceScale = this.getActivePriceScale();
                TerceraChartUtils.DrawPointer(gr, this, priceScale, pointer, this.pointerHeight);
            }
            drawPointers.splice(0, len);
        }
    }
    // TODO. Replace this.Renderers with main thread renderers.
    // parameter is name of Class
    GetMainThreadRenderer(T) {
        const res = null;
        const arr = this.Renderers;
        let i;
        const len = arr.length;
        for (i = 0; i < len; i++) {
            if (arr[i].ClassNameStorage[T]) {
                return arr[i];
            }
        }
        return res;
    }
    LayoutPaddingRenderers() {
        if (this.PaddingRenderers.length == 0) {
            return;
        }
        this.RendPaddingTop = 0;
        this.RendPaddingBottom = 0;
        this.RendPaddingLeft = 0;
        this.RendPaddingRight = 0;
        const clientRect = this.ClientRectangle;
        this.RendPaddingTop += clientRect.Top(); // - leftRenderer.Top;
        this.RendPaddingBottom += clientRect.Bottom(); // - leftRenderer.Bottom;
        this.RendPaddingLeft += clientRect.Left(); // - leftRenderer.Left;
        this.RendPaddingRight += clientRect.Right(); // - leftRenderer.Right;
    }
    Dispose() {
        this.rightYScaleRenderer = null;
        this.leftYScaleRenderer = null;
        const allRenderers = this.GetAllRenderers();
        for (const renderer of allRenderers) {
            renderer.Dispose();
        }
        this.Renderers = [];
        this.MainThreadRenderers = [];
        this.PaddingRenderers = [];
    }
    getWindowHeaderRectangle() {
        let WindowHeaderRectangle;
        if (this.HeaderVisible) {
            WindowHeaderRectangle = new Rectangle(this.Rectangle.X + (this.PaddingLeft > 0 ? this.PaddingLeft : 0), this.Rectangle.Y, this.ClientRectangle.Width, this.IsMainWindow ? this.HeaderHeight : this.IndicatorHeaderHeight);
        }
        else {
            WindowHeaderRectangle = Rectangle.Empty();
        }
        return WindowHeaderRectangle;
    }
    ComputeZoomLevels() {
        const levels = [];
        const coefficient = Math.pow(this.MAX_X_SCALE / this.MIN_X_SCALE, 1 / (this.NUM_ZOOM_LEVELS - 1));
        let currentScale = this.MIN_X_SCALE;
        for (let i = 0; i < this.NUM_ZOOM_LEVELS; i++) {
            levels.push(currentScale);
            currentScale *= coefficient;
        }
        return levels;
    }
    FindClosestZoomLevelIndex(scale) {
        return this.ZOOM_LEVELS.reduce((closestIndex, level, index) => {
            return Math.abs(level - scale) < Math.abs(this.ZOOM_LEVELS[closestIndex] - scale) ? index : closestIndex;
        }, 0);
    }
    ActiveZoomXStepSize(forceStep) {
        const baseStep = this.MIN_X_SCALE_LEVEL; // Minimum step size
        const scaleFactor = 0.05; // Controls how step size grows with XScale
        return baseStep + this.XScale * scaleFactor;
    }
    ZoomIn(forceLevel = false) {
        if (forceLevel) {
            const currentIndex = this.FindClosestZoomLevelIndex(this.XScale);
            if (currentIndex < this.ZOOM_LEVELS.length - 1) {
                this.CheckAndSetXScale(this.ZOOM_LEVELS[currentIndex + 1]);
            }
        }
        else {
            const newXScale = this.XScale + this.ActiveZoomXStepSize(forceLevel);
            this.CheckAndSetXScale(newXScale);
        }
    }
    ZoomOut(forceLevel = false) {
        if (forceLevel) {
            const currentIndex = this.FindClosestZoomLevelIndex(this.XScale);
            if (currentIndex > 0) {
                this.CheckAndSetXScale(this.ZOOM_LEVELS[currentIndex - 1]);
            }
        }
        else {
            const newXScale = this.XScale - this.ActiveZoomXStepSize(forceLevel);
            this.CheckAndSetXScale(newXScale);
        }
    }
    ZoomX(delta) {
        return false;
    }
    ZoomY(delta) {
        return false;
    }
    MoveX(minXDelta, maxXDelta) {
        return false;
    }
    MoveY(newMinY, newMaxY) {
        if (Math.abs(newMaxY) < this.SCALE_LIMIT && Math.abs(newMinY) < this.SCALE_LIMIT) {
            this.FmaxFloatY = newMaxY;
            this.FminFloatY = newMinY;
            this.CalcScales();
        }
        return true;
    }
    CheckAndSetXScale(newXScale) {
        // max
        if (newXScale > this.MAX_X_SCALE) {
            newXScale = this.MAX_X_SCALE;
        }
        // min
        if (newXScale < this.MIN_X_SCALE) {
            newXScale = this.MIN_X_SCALE;
        }
        this.XScale = newXScale;
    }
    CanZoomIn() {
        return this.XScale < this.MAX_X_SCALE;
    }
    CanZoomOut() {
        return this.XScale > this.MIN_X_SCALE;
    }
    ZoomAll() {
        this.CheckAndSetXScale(this.MIN_X_SCALE);
    }
    drawWindowHeader(gr, curLayer) {
        if (!this.HeaderVisible || curLayer !== LayersEnum.Main || this.IsMainWindow) {
            return;
        }
        const borderW = this.subHeaderBorderWidth;
        const border = new Pen(ThemeManager.colors.chartCommonBorder, borderW);
        gr.RoundRectangle(this.windowHeaderRectangle, this.headerBorderRadius, this.IndicatorBackBrush, border);
        gr.save();
        const path = new Path2D();
        const headerRect = this.windowHeaderRectangle;
        path.roundRect(headerRect.X + borderW, headerRect.Y + borderW, headerRect.Width - borderW * 2, headerRect.Height - borderW * 2, 4);
        gr.clip(path);
        this.drawCollapsedBtn(gr);
        gr.restore();
    }
    drawCollapsedBtn(gr) {
        if (this.collapserRectangle.IsEmpty()) {
            return;
        }
        const isHovered = this.isCollapsedHover;
        let image;
        if (this.Collapsed) {
            image = isHovered ? this.collapserWindowHoveredImage.image : this.collapserWindowDefaultImage.image;
        }
        else {
            image = isHovered ? this.expanderWindowHoveredImage.image : this.expanderWindowDefaultImage.image;
        }
        const brush = new SolidBrush(isHovered
            ? ThemeManager.colors.chartPlateCollapsedBtnHover
            : ThemeManager.colors.chartPlateCollapsedBtnDefault);
        gr.RoundRectangle(this.collapserRectangle, 4, brush);
        gr.DrawCenteredImage(image, this.collapserRectangle);
    }
    getActivePriceScale() {
        var _a;
        return (_a = this.rightYScaleRenderer) !== null && _a !== void 0 ? _a : this.leftYScaleRenderer;
    }
}
